/**
 * School Teacher Management - Tab 4 of School Admin Dashboard
 *
 * Displays teacher roster with adoption metrics:
 * - Teachers from Schoology (via lms_courses.teacher_user_id)
 * - Courses taught
 * - Student adoption rates
 * - Last login / active status
 * - Subjects taught
 *
 * Data Source: GET /api/admin/school/teachers (session-based - no ID)
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';

// Tappy Brand Colors - Official 2025 Design System
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkGray: '#3D4954',
    mediumGray: '#8D7F88',
    lightTeal: '#ABCACA',
    paleGray: '#D5DEDD',
    lightGray: '#ECF3F2',
    veryLight: '#F5F9FA',
    almostWhite: '#FCFDFE',
  },
  semantic: {
    success: '#42909E',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#6DB4CD',
  }
};

function SchoolTeacherManagement({ getApiUrl, isDistrictAdminView }) {
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  // Helper to get API URL - use prop if provided, otherwise default to session-based
  const buildApiUrl = (endpoint) => {
    if (getApiUrl) {
      return getApiUrl(endpoint);
    }
    return `/api/admin/school/${endpoint}`;
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('teachers'));
      setTeachers(response.data.teachers || []);
      setSummary(response.data.summary || {});
      setError(null);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError(err.response?.data?.error || 'Failed to load teacher data');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: '#d1fae5', text: '#065f46', label: 'Active' };
      case 'recent': return { bg: '#fef3c7', text: '#92400e', label: 'Recent' };
      case 'inactive': return { bg: '#fee2e2', text: '#991b1b', label: 'Inactive' };
      default: return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown' };
    }
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: TappyBrandColors.accent.coral + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: TappyBrandColors.accent.coral
        }}>!</div>
        <h3 style={{ color: TappyBrandColors.accent.coral, marginBottom: '12px', fontWeight: 600 }}>Error Loading Teachers</h3>
        <p style={{ color: TappyBrandColors.neutral.mediumGray, marginBottom: '20px' }}>{error}</p>
        <button
          onClick={fetchTeachers}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Teacher Management
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Monitor teacher adoption and engagement with Tappy
          </p>
        </div>
        <button
          onClick={fetchTeachers}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#4F46E5', marginBottom: '8px' }}>
              {summary.total_teachers || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>Total Teachers</div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#10b981', marginBottom: '8px' }}>
              {summary.active_teachers || 0}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>Active Teachers</div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
              Last 7 days
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#f59e0b', marginBottom: '8px' }}>
              {summary.avg_adoption_pct || 0}%
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>Avg Adoption Rate</div>
            <div style={{
              marginTop: '12px',
              height: '4px',
              backgroundColor: '#e5e7eb',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${summary.avg_adoption_pct || 0}%`,
                backgroundColor: '#f59e0b',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Teacher Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <p style={{ color: '#6b7280' }}>Loading teachers...</p>
          </div>
        ) : teachers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>👨‍🏫</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
              No Teachers Found
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              No teachers are currently assigned to courses at this school
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Teacher Name
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Subjects
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Courses
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Students
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Active
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Adoption
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Last Login
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher, index) => {
                  const status = getStatusColor(teacher.status);
                  return (
                    <tr
                      key={teacher.id}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                          {teacher.name || teacher.username}
                        </div>
                        {teacher.email && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                            {teacher.email}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: '13px', color: '#6b7280', maxWidth: '200px' }}>
                          {teacher.subjects || 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                        {teacher.courses_count || 0}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
                          {teacher.students_assigned || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          assigned
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '14px', fontWeight: 500, color: '#10b981' }}>
                        {teacher.students_active || 0}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
                          {teacher.adoption_pct || 0}%
                        </div>
                        <div style={{
                          marginTop: '6px',
                          height: '4px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '2px',
                          overflow: 'hidden',
                          width: '60px',
                          margin: '6px auto 0'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${teacher.adoption_pct || 0}%`,
                            backgroundColor: (teacher.adoption_pct || 0) >= 80 ? '#10b981' : (teacher.adoption_pct || 0) >= 50 ? '#f59e0b' : '#ef4444',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: '13px', color: '#6b7280' }}>
                        {formatTimeAgo(teacher.last_login)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          backgroundColor: status.bg,
                          color: status.text,
                          borderRadius: '16px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
          <strong>Status indicators:</strong>
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
          <li><strong>Active:</strong> Logged in within the last 7 days</li>
          <li><strong>Recent:</strong> Logged in within the last 30 days</li>
          <li><strong>Inactive:</strong> No login in the last 30 days</li>
        </ul>
        <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>
          <strong>Adoption rate:</strong> Percentage of assigned students who are actively using Tappy (active in last 30 days)
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        backgroundColor: '#dbeafe',
        borderRadius: '8px',
        border: '1px solid #3b82f6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: TappyBrandColors.primary.skyBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white',
            flexShrink: 0
          }}>i</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: TappyBrandColors.primary.darkTeal, marginBottom: '4px' }}>
              Want to increase teacher adoption?
            </div>
            <div style={{ fontSize: '13px', color: TappyBrandColors.primary.teal }}>
              We offer free training sessions for teachers. Contact care@teachtappy.com to schedule.
            </div>
          </div>
          <button
            onClick={() => {
              const subject = encodeURIComponent('Teacher Training Request');
              const body = encodeURIComponent(
                `Hi Tappy Team,\n\n` +
                `I'd like to schedule a training session for our teachers.\n\n` +
                `Details:\n` +
                `- Number of teachers: ${summary?.total_teachers || 'N/A'}\n` +
                `- Current adoption rate: ${summary?.adoption_pct || 0}%\n\n` +
                `Please let me know available times.\n\n` +
                `Thank you!`
              );
              window.location.href = `mailto:care@teachtappy.com?subject=${subject}&body=${body}`;
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            Schedule Training
          </button>
        </div>
      </div>
    </div>
  );
}

export default SchoolTeacherManagement;
